// The runtime, which is deliberately the smallest part.
//
// # MessageFormat 2.0, because it is the standard and the messages outlive the code
//
// MF2 is **stable in CLDR** — approved by the CLDR Technical Committee, normative in
// TR35, recommended for adoption. That is the durable half of this decision: every
// message written in `catalogue.ts` is written in a format that is not going to be
// replaced.
//
// The *runtime* is the temporary half. `Intl.MessageFormat` is a TC39 proposal at
// **stage 1** and ships in nothing, so `messageformat@4` — which is that proposal's
// own polyfill, by its champion, with no dependencies — stands in for it. The day
// the engine has it, this file loses an import and the catalogue does not change by
// one character. That is the whole reason for choosing a format first and a library
// second.
//
// # Bidi isolation stays on
//
// MF2 wraps placeholders in U+2068/U+2069 by default so a right-to-left value cannot
// reorder the sentence around it. An Arabic or Hebrew name inside a Turkish sentence
// is an ordinary case in this product, not an exotic one, so the isolates are kept
// even though both shipped locales are left-to-right. Tests strip them rather than
// asserting their absence.

import { MessageFormat } from "messageformat";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useLayoutEffect } from "react";
// `MessageKey` comes from the catalogue and that is safe: it is a TYPE, and a type is erased
// before a bundler ever sees it. The four VALUES below come from `./locales` and not from
// `./catalogue`, which is the whole of the bundle fix — they are needed at runtime and both
// applications reach this file, so importing them from the catalogue makes the catalogue
// reachable from the PUBLIC careers entry, and rollup assigns a module to a chunk as a unit,
// so all 1223 keys go with it.
import type { MessageKey } from "./catalogue";
import { DEFAULT_LOCALE, formattingTag, LOCALES, type Locale } from "./locales";

export type { MessageKey } from "./catalogue";
export { DEFAULT_LOCALE, formattingTag, LOCALES, type Locale } from "./locales";

/**
 * Values a message may interpolate — and deliberately **not** a `Date`.
 *
 * MF2 formats a placeholder through a function chosen by its annotation, and a bare
 * `{$date}` has none, so the fallback is the value's own string form. A `Date`'s
 * string form is `Thu Aug 06 2026 03:00:00 GMT+0300 (Türkiye Standart Saati)`, and
 * that is what /organisation printed on screen under a Turkish heading.
 *
 * It could be fixed by annotating the pattern — `{$date :datetime style=medium}` —
 * and that would be wrong here for a reason beyond this one message: riksu already
 * decided how a date is written (`formatDate`, `timeZone: "UTC"`, short month), and a
 * second answer inside the catalogue would drift from the first the day either
 * changed. The catalogue holds SENTENCES. A date arrives already written.
 *
 * So the type refuses one, and the mistake stops compiling instead of shipping.
 */
export type Values = Record<string, string | number>;

/** Formats one message. */
export type Translate = (key: MessageKey, values?: Values) => string;

/**
 * The messages a BUNDLE holds, supplied by its entry rather than imported here.
 *
 * # WHY THIS IS INJECTED AND NOT AN IMPORT
 *
 * It was `import { CATALOGUES } from "./catalogue"`, one line, and that line put the
 * WHOLE PRODUCT CATALOGUE — 146.3 kB, 1223 keys in two locales — into every chunk that
 * reaches `useT`. The two entries share one chunk, so the surface that paid for it was
 * the one that must not: **`careers.html` is public, anonymous and indexable, and it uses
 * FOUR keys.** Measured on the built output, the chunk a job applicant downloads contained
 * "payroll" 129 times, "zimmet" 40, "rapor" 13, "kıdem" 6 and "KVKK" 3.
 *
 * `vite.config.ts` already says the two applications are separate bundles "because
 * shipping the product surface to an anonymous visitor is both a payload and a surface
 * problem". The split was built for exactly this and one static import defeated it — so
 * the fix belongs at that import rather than in a chunking option, which cannot help: a
 * `manualChunks` line still leaves `careers.js` REFERENCING the catalogue, and a reference
 * is what a download is.
 *
 * The small locale metadata lives in `./locales`, so this runtime does not make either
 * catalogue reachable. The careers entry installs only its public messages; the product
 * entry dynamically imports the selected language and installs it before rendering. A
 * later choice installs its language before changing context. Measured after that split,
 * the complete static JavaScript graph fell from about 2.72 MB / 749 kB gzip to 867 kB /
 * 276 kB gzip, with one 71–76 kB gzip catalogue fetched for the active language. The
 * smaller 492/155 figure printed for `app.js` alone excludes its required shared runtime
 * and must not be reported as the browser's startup cost.
 *
 * # WHAT THIS DOES NOT WEAKEN
 *
 * Both halves of "a missing translation is a type error" are untouched, because neither
 * ever lived here. `tr` is declared `Record<MessageKey, string>` in `catalogue.ts`, so a
 * key in one locale and not the other still fails `tsc` there; and `Translate` still takes
 * a `MessageKey`, so a call site still cannot invent one. What injection adds is a third
 * question those two never asked — *does the bundle that is RUNNING hold the key this
 * bundle CALLS* — and that is answered per entry by a test that renders the surface and
 * fails on the fallback below, rather than by a type this file could not express anyway.
 */
export type Messages = Readonly<
  Partial<Record<Locale, Readonly<Partial<Record<MessageKey, string>>>>>
>;

/**
 * What this bundle can say, set once by the entry before anything renders.
 *
 * Module scope rather than context, because {@link translate} is called OUTSIDE a
 * component — a document title, an attribute, a log — and a context could not reach it.
 */
let installed: Messages = {};

/**
 * Compiled messages, cached per locale and key.
 *
 * Compiling is parsing, and parsing the same pattern on every render of a table with
 * two hundred rows is the cost that makes people reach for concatenation instead.
 *
 * Declared here rather than beside {@link format} because {@link installMessages} has to
 * empty it, and a cache that outlives the thing it is a cache OF is the defect below.
 */
const compiled = new Map<string, MessageFormat>();

/**
 * Installs the messages for this bundle. Called by an entry, and by nothing else.
 *
 * Idempotent and last-wins, which is what lets a test install a fixture without unmounting
 * the world. It is deliberately NOT additive: two entries in one process is not a thing
 * that happens, and merging would hide an entry that installed the wrong set.
 *
 * # IT EMPTIES THE COMPILED CACHE, and that line is not tidiness
 *
 * `compiled` is derived from `installed`, so leaving it in place makes an install a
 * PARTIAL one: every key already compiled goes on answering from the previous set. In the
 * product that is invisible — an entry installs once before anything renders — which is
 * exactly what makes it the expensive kind of latent defect, and it was found by the
 * control in `careers.test.tsx` rather than by reading this file. That test installs an
 * EMPTY set and asserts the surface falls back to printing its keys; it failed, reporting
 * `expected 'Open positions' to be 'careers.heading'`, because the two tests before it had
 * already compiled that key. The mechanism was serving messages nobody had installed.
 */
export function installMessages(messages: Messages): void {
  installed = messages;
  compiled.clear();
}

/**
 * Add or replace one locale without evicting languages already loaded in this session.
 *
 * The product entry loads only its initial language. A later language choice must install
 * the next catalogue before switching the context, while keeping the previous one available
 * for an immediate switch back. `installMessages` deliberately remains last-wins for bundle
 * entries and isolated tests; this is the additive operation used by the product loader.
 */
export function installLocaleMessages(
  locale: Locale,
  messages: Readonly<Partial<Record<MessageKey, string>>>,
): void {
  installed = { ...installed, [locale]: messages };
  const prefix = `${locale}\u0000`;
  for (const key of compiled.keys()) {
    if (key.startsWith(prefix)) compiled.delete(key);
  }
}

const LocaleContext = createContext<Locale>(DEFAULT_LOCALE);

function format(locale: Locale, key: MessageKey, values?: Values): string {
  // U+0000 SEPARATES THE TWO HALVES, because it is the one character that can occur in
  // neither a BCP 47 tag nor a message key — so `tr` + `x.y` cannot collide with `tr.x` + `y`.
  //
  // WRITTEN AS THE ESCAPE AND NEVER AS THE BYTE. A literal 0x00 here makes the whole file
  // binary to git, which reports it as `Bin 8484 -> 14759 bytes` and shows no diff at all —
  // so every later change to this file becomes unreviewable, silently: `tsc`, `biome` and
  // the suite are all green either way, and nothing in the gate set can see it.
  const cacheKey = `${locale}\u0000${key}`;
  let message = compiled.get(cacheKey);
  if (!message) {
    const pattern = installed[locale]?.[key];
    // A KEY THIS BUNDLE DOES NOT HOLD MUST NOT TAKE THE SCREEN DOWN.
    //
    // `MessageKey` makes that impossible for a literal, and there is exactly one place
    // it cannot reach: a key the SERVER declares. riksu publishes expense categories
    // and their fields as declarations carrying a `label_key` — which is the whole
    // point, what a company may claim being data rather than a Record in this bundle —
    // and the call site has to cast, because the server's string is not statically one
    // of ours.
    //
    // So the two halves drift, and the failure was total rather than partial: twelve
    // declared keys were missing, `new MessageFormat(locale, undefined)` threw
    // `Cannot read properties of undefined (reading 'declarations')` from four frames
    // inside a parser, the boundary caught it, and /expense rendered "this screen
    // stopped" — naming neither the key nor the locale.
    //
    // AND THERE IS A SECOND WAY IN NOW, which is the cost of injecting the messages:
    // an entry can install a set that does not hold a key its own screens call. The
    // careers entry installs four messages on purpose, so this is a real path there
    // rather than a theoretical one — which is why `careers.test.tsx` renders that
    // surface in BOTH locales and fails on a rendered string that equals its own key.
    // The fallback is what makes that test possible: a throw would be caught by a
    // boundary and read as a broken screen instead of a missing word.
    //
    // THE KEY ITSELF is the fallback, and deliberately not a blank and not a guess: it
    // is visible on the screen, it names exactly what is missing, and it is a string
    // somebody can search for. `tools/check-messages.mjs` fails the build on a missing
    // one, so this path is a seatbelt rather than a licence to leave one out.
    if (pattern === undefined) {
      return key;
    }
    // THE FORMATTING TAG, not the catalogue key. A `:number` inside a pattern is a
    // formatted number, so it follows the same region every other figure on the screen
    // does. Measured identical between `en` and `en-GB` here — separators and plural
    // rules both — so this changes nothing today and stops the two answers from parting
    // company the first time a locale is added whose region does matter.
    message = new MessageFormat(formattingTag(locale), pattern);
    compiled.set(cacheKey, message);
  }
  return message.format(values);
}

/** Narrows anything to a locale this product actually ships. */
export function asLocale(raw: string | null | undefined): Locale | null {
  if (!raw) {
    return null;
  }
  // BCP 47: `tr-TR` and `tr` are the same language for our purposes, and a browser
  // sends the first far more often than the second.
  const primary = raw.toLowerCase().split("-")[0];
  return LOCALES.find((locale) => locale === primary) ?? null;
}

export interface LocaleProviderProps {
  readonly locale: Locale;
  readonly children: ReactNode;
}

/**
 * Supplies the locale and keeps `<html lang>` truthful.
 *
 * The attribute is not decoration: it is what a screen reader picks a voice from and
 * what `:lang()` and hyphenation rules key on. A page that renders Turkish under
 * `lang="en"` is read aloud in an English accent. It is also what CSS `text-transform`
 * keys on — the property is language-sensitive by spec, with Turkish `i` → `İ` as the
 * spec's own example — and `module-nav.tsx` uppercases its module heading, so the
 * attribute decides how that heading is CASED and not only how it is pronounced.
 *
 * A LAYOUT EFFECT, not a `useMemo`. Writing to `document` during render is a side
 * effect where React allows none: a render that is thrown away — and a concurrent
 * render legitimately is — would still have performed it, so the attribute could end up
 * describing a tree that was never committed. `useLayoutEffect` runs after the commit
 * and BEFORE paint, which is the ordering the casing above needs; a plain `useEffect`
 * would let one frame paint with the previous language's casing rules.
 */
export function LocaleProvider({ locale, children }: LocaleProviderProps) {
  useLayoutEffect(() => {
    document.documentElement.lang = formattingTag(locale);
  }, [locale]);

  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

/**
 * The reader's locale as a BCP 47 tag, for `Intl`.
 *
 * **This answers `en-GB` or `tr-TR`, not the catalogue key**, because every caller in
 * the product does exactly one thing with it: hands it to a formatter or a collator.
 * Resolving the region here rather than at each of those call sites is the difference
 * between a rule that holds and a rule the next screen forgets — and it forgets
 * silently, because a bare `en` formats a date perfectly well, just in the wrong order.
 *
 * The catalogue key stays inside this module. `useT` reads it from the context
 * directly, and anything that needs the LANGUAGE rather than the formatting tag — the
 * switcher, a URL segment — asks {@link useLanguage}.
 */
export function useLocale(): string {
  return formattingTag(useContext(LocaleContext));
}

/** The reader's language, as the catalogue and the URL spell it: `en` or `tr`. */
export function useLanguage(): Locale {
  return useContext(LocaleContext);
}

/** The translator. */
export function useT(): Translate {
  const locale = useContext(LocaleContext);
  return useCallback((key: MessageKey, values?: Values) => format(locale, key, values), [locale]);
}

/** Formats a message outside a component — for a title, a document attribute, a log. */
export function translate(locale: Locale, key: MessageKey, values?: Values): string {
  return format(locale, key, values);
}
